import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { useTenderAdminModeStore } from "@/stores/tender-admin-mode-store"

import { TENDER_QUERY_KEYS } from "../hooks/use-tender-cases"

/**
 * Toggle for the per-browser tender admin mode. Rendered only for tender admins
 * (the membership flag in user_policy); turning it on opts into elevated access,
 * off restores the normal involvement-based experience. Because access source /
 * role are computed server-side from the request header, flipping it invalidates
 * the tender queries so the list/detail refetch with the new privileges.
 *
 * `variant="row"` renders a labelled switch (for the sidebar popover header);
 * `variant="icon"` renders a compact toggle button (for the expanded header);
 * `variant="banner"` renders the landing-page pill with state text + switch.
 */
export function TenderAdminModeToggle({
  variant = "icon",
}: {
  variant?: "icon" | "row" | "banner"
}) {
  const { t } = useTranslation()
  const isTenderAdmin = SettingsService.getInstance().settings.user_policy.is_tender_admin
  const enabled = useTenderAdminModeStore((s) => s.enabled)
  const setEnabled = useTenderAdminModeStore((s) => s.setEnabled)
  const queryClient = useQueryClient()

  if (!isTenderAdmin) return null

  const apply = (next: boolean) => {
    setEnabled(next)
    void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.casePrefix })
    void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.participantsPrefix })
    // CV-match (unlike the open-read case/participant endpoints) returns 403 for a
    // non-involved viewer once admin mode is off. invalidateQueries would refetch
    // but keep the last successful data on a failed refetch, so stale candidate
    // names would keep rendering. Drop the cache outright instead (same as
    // useResetCvMatch) so nothing stale survives the toggle.
    queryClient.removeQueries({ queryKey: TENDER_QUERY_KEYS.cvMatchPrefix })
  }

  if (variant === "banner") {
    return (
      <div
        className={cn(
          "mt-5 inline-flex max-w-xl items-center gap-2 rounded-full border px-3 py-1.5 text-xs",
          enabled
            ? "border-accent/60 bg-accent/60 text-accent-foreground dark:border-accent/40 dark:bg-accent/20 dark:text-accent-foreground"
            : "text-muted-foreground"
        )}
      >
        <ShieldCheck className="size-3.5 shrink-0" />
        <span>
          {enabled ? t("tender.landing.adminModeBanner") : t("tender.landing.adminModeBannerOff")}
        </span>
        <Switch
          aria-label={t("tender.sidebar.adminMode")}
          checked={enabled}
          className="ml-1"
          onCheckedChange={apply}
        />
      </div>
    )
  }

  if (variant === "row") {
    return (
      <div className="ml-auto flex items-center gap-2">
        <ShieldCheck
          className={cn("size-3.5", enabled ? "text-accent-foreground" : "text-muted-foreground")}
        />
        <Label className="cursor-pointer text-xs font-medium" htmlFor="tender-admin-mode">
          {t("tender.sidebar.adminMode")}
        </Label>
        <Switch
          aria-label={t("tender.sidebar.adminMode")}
          checked={enabled}
          id="tender-admin-mode"
          onCheckedChange={apply}
        />
      </div>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          aria-label={t("tender.sidebar.adminMode")}
          aria-pressed={enabled}
          size="icon"
          type="button"
          variant="ghost"
          className={cn(
            "size-7 rounded-full p-0",
            enabled ? "bg-accent/20 text-accent-foreground" : "text-muted-foreground"
          )}
          onClick={(e) => {
            e.stopPropagation()
            apply(!enabled)
          }}
        >
          <ShieldCheck className="size-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {enabled ? t("tender.sidebar.adminModeOnTooltip") : t("tender.sidebar.adminModeOffTooltip")}
      </TooltipContent>
    </Tooltip>
  )
}
